import type { Appointment, EpisodeOfCare } from '@medplum/fhirtypes';
import { codeFromServiceTypeCodeableConcept } from '@/lib/fhirValueSets/sessionAndEpisodeTypes';
import { isCohortGroupAppointment } from '@/lib/groupTherapy/groupSharedNote';
import { isGroupTherapyEpisodeOfCare } from '@/lib/groupTherapy/groupEnrollment';
import { getServiceTypeCode } from '@unify/fhir-model/resources/episode-of-care';

export type SessionEpisodeType =
  | 'individual'
  | 'group'
  | 'family'
  | 'couples'
  | 'medication-management'
  | undefined;

export function resolveSessionEpisodeType(
  episodeOfCare: EpisodeOfCare | null | undefined,
): SessionEpisodeType {
  if (!episodeOfCare) return undefined;
  const code =
    getServiceTypeCode(episodeOfCare) ??
    codeFromServiceTypeCodeableConcept(episodeOfCare.type?.[0]);
  if (
    code === 'individual' ||
    code === 'group' ||
    code === 'family' ||
    code === 'couples' ||
    code === 'medication-management'
  ) {
    return code;
  }
  // Legacy / alternate coding used by some Group Therapy EoCs
  if (code === 'group-therapy') return 'group';
  return undefined;
}

export function isGroupTherapyEpisode(episodeOfCare: EpisodeOfCare | null | undefined): boolean {
  if (!episodeOfCare) return false;
  return resolveSessionEpisodeType(episodeOfCare) === 'group' || isGroupTherapyEpisodeOfCare(episodeOfCare);
}

/** Group Therapy Zoom/in-person sidebar mode: cohort Appointment and/or Group Therapy EoC. */
export function isGroupTherapySession(
  appointment: Appointment | null | undefined,
  episodeOfCare: EpisodeOfCare | null | undefined,
): boolean {
  if (appointment && isCohortGroupAppointment(appointment)) return true;
  return isGroupTherapyEpisode(episodeOfCare);
}

export function isSharedTherapyEpisode(episodeOfCare: EpisodeOfCare | null | undefined): boolean {
  const type = resolveSessionEpisodeType(episodeOfCare);
  return type === 'couples' || type === 'family';
}
